import { PlusOutlined } from '@ant-design/icons'
import { Button, Checkbox, Flex, Form, Modal } from 'antd'
import { ModalProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import {
  useClassifierServiceGetInvestigationClassifierListEndpoint,
  useInvestigationsServiceCreateScreeningInvestigationEndpoint,
  useInvestigationsServiceGetListSpecimensEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  Countries,
  ListSortDirection,
  ScreeningTestItemDto,
  SearchSpecimenSortBy
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { ClassifierInfiniteSelect, message, SaveConfirm } from 'src/components'
import { useSessionContext } from 'src/contexts'
import { HivDoctorSelect } from 'src/features/hiv-anamnesis/components/fields/HivDoctorSelect'
import { usePatientBreadcrumb } from 'src/features/patients/hooks'
import { parseCategories } from 'src/features/persons/utils'
import { useClassifierId, useUser } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { InvestigationCategories } from './InvestigationCategories'
import {
  newScreeningTestRow,
  SCREENING_TESTS_FORM,
  ScreeningSupplyRow,
  ScreeningTestRow,
  ScreeningTestRowValues
} from './ScreeningTestRow'

type Props = {
  onCancel: () => void
  onSuccess: () => void
} & ModalProps

export const InvestigationDemandModalScreening = ({
  onCancel,
  onSuccess,
  ...props
}: Props) => {
  const { t, currentLang } = useLocales()
  const [form] = Form.useForm()
  const { personId, id } = useParams()
  const { session } = useSessionContext()
  const { userData } = useUser()

  const { person } = usePatientBreadcrumb(id)

  const isForeign = person?.citizenship !== Countries.MD
  const preTestCounseling = Form.useWatch('preTestCounseling', form)
  const postTestCounseling = Form.useWatch('postTestCounseling', form)

  const reasonClassifierId = useClassifierId(
    ClassifierType.TESTING_CONTINGENT,
    ClassifierCategory.SCREENING
  )

  const resultOptionClassifierId = useClassifierId(
    ClassifierType.INVESTIGATION_RESULT_OPTION,
    ClassifierCategory.INVESTIGATIONS
  )
  const cuatmClassifierId = useClassifierId(
    ClassifierType.SPECIMEN_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )

  const { data: specimens } = useInvestigationsServiceGetListSpecimensEndpoint({
    pageNumber: 1,
    pageSize: 1,
    personIds: [String(id)],
    sortBy: SearchSpecimenSortBy.CREATED_AT,
    sortDirection: ListSortDirection.DESCENDING,
    institutionIds: [String(userData?.currentLegalEntityId)]
  })

  const investigationReasonId = Form.useWatch('investigationReasonId', form)
  const categoriesForm = Form.useWatch('categories', form)
  const specimenTypeId = Form.useWatch('specimenTypeId', form)
  const testedBy = Form.useWatch('testedBy', form)
  const testRows: ScreeningTestRowValues[] | undefined = Form.useWatch(
    SCREENING_TESTS_FORM,
    form
  )

  // Every row needs its type, test type, result and date before confirming.
  const areTestsComplete =
    !!testRows?.length &&
    testRows.every(
      (row) =>
        !!row?.investigationTypeId &&
        !!row?.testTypeId &&
        !!row?.resultOptionId &&
        !!row?.resultDate
    )
  const hasAnyResult = !!testRows?.some((row) => !!row?.resultOptionId)

  // Specimen type or category changes invalidate every test row.
  const resetTests = () =>
    form.setFieldValue(SCREENING_TESTS_FORM, [newScreeningTestRow()])

  const selectedCategory = useMemo(() => {
    const parsedCategories = parseCategories({ categories: categoriesForm })

    const hasSelectedInvestigation = parsedCategories.some(
      (item) => item?.institutionId
    )

    return {
      hasSelectedInvestigation,
      parsedCategories
    }
  }, [investigationReasonId, categoriesForm])

  const { mutate: createScreeningInvestigationRequest, isPending } =
    useInvestigationsServiceCreateScreeningInvestigationEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        onCancel()
        onSuccess?.()
      }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const categories = parseCategories({ categories: values.categories })

    if (categories.length === 0) {
      return message.error(t('laboratory_classifiers.select_category_error'))
    }

    const firstCategoryJson = values.categories?.[0]?.currentInstitution?.[0]
    const firstCategory = firstCategoryJson ? JSON.parse(firstCategoryJson) : {}

    const items: ScreeningTestItemDto[] = (
      (values[SCREENING_TESTS_FORM] ?? []) as ScreeningTestRowValues[]
    ).map((row) => ({
      investigationTypeId: String(row.investigationTypeId),
      testTypeId: String(row.testTypeId),
      resultOptionId: String(row.resultOptionId),
      resultValueOptionId: row.resultValueOptionId || null,
      resultValue: row.resultValue ?? null,
      resultDate: dayjs.utc(row.resultDate).tz().format(yearMonthDaySlash),
      supplies: isForeign
        ? []
        : (row.supplies ?? [])
            .filter(
              (item: ScreeningSupplyRow) =>
                !!item?.internalInventoryItemId && !!item?.productFormId
            )
            .map((item: ScreeningSupplyRow) => ({
              quantity: item.dispensedAmount,
              unitId: String(item.productFormId),
              productId: String(item.internalInventoryItemId)
            }))
    }))

    const payload = {
      institutionId: String(userData?.currentLegalEntityId),
      personId: String(personId || id),
      doctorId: String(session?.userId),
      investigationReasonId: values?.investigationReasonId,
      secondaryInvestigationReasonIds: values?.secondaryInvestigationReasonIds,
      categoryId: firstCategory?.categoryId || '',
      specimenTypeId: specimenTypeId || '',
      items,
      testedBy: String(values.testedBy),
      preTestCounseling: preTestCounseling,
      postTestCounseling: hasAnyResult ? postTestCounseling : undefined
    }

    createScreeningInvestigationRequest({
      requestBody: payload
    })
  }

  // Screening offers only the active rapid-test investigation types of the
  // selected category that accept the chosen specimen type.
  const screeningCategoryId = selectedCategory.parsedCategories?.[0]?.categoryId

  const { data: investigationsTypes } =
    useClassifierServiceGetInvestigationClassifierListEndpoint(
      {
        pageNumber: 1,
        pageSize: 100,
        isActive: true,
        isRapidTest: true,
        categoryIds: [screeningCategoryId],
        specimenTypeIds: specimenTypeId ? [specimenTypeId] : undefined
      },
      undefined,
      { enabled: !!screeningCategoryId }
    )

  // The rows' investigation types belong to the category: start over when it
  // changes (also when it is unchecked and another one is picked).
  useEffect(() => {
    resetTests()
  }, [screeningCategoryId])

  const investigationTypeOptions = (investigationsTypes?.data ?? []).map(
    (item) => ({
      label: item?.title?.[currentLang?.value],
      value: item.id
    })
  )

  useEffect(() => {
    if (specimens?.data && specimens?.data.length > 0) {
      const latestSpecimen = specimens?.data[0]
      form.setFieldsValue({
        investigationReasonId: latestSpecimen?.demand?.investigationReason?.id,
        secondaryInvestigationReasonIds:
          latestSpecimen?.demand?.secondaryInvestigationReasons?.map(
            (reason) => reason.id
          ) || []
      })
    }
  }, [specimens])

  return (
    <Modal
      width={800}
      open
      centered
      maskClosable={false}
      onCancel={onCancel}
      title={t('consultations.new_investigation_request')}
      footer={null}
      styles={{
        body: {
          maxHeight: '70vh',
          overflowY: 'auto'
        }
      }}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="preTestCounseling"
          rules={requiredRules}
          valuePropName="checked"
        >
          <Checkbox>{t('cases.pre_test_counseling')}</Checkbox>
        </Form.Item>

        {preTestCounseling && (
          <Flex vertical gap={16}>
            <Flex vertical gap={24}>
              {reasonClassifierId && (
                <Form.Item
                  name="investigationReasonId"
                  label={t('cases.primary_request_reason')}
                  rules={requiredRules}
                >
                  <ClassifierInfiniteSelect
                    id={reasonClassifierId}
                    placeholder={t('cases.primary_request_reason')}
                  />
                </Form.Item>
              )}

              {investigationReasonId && (
                <Form.Item
                  name="secondaryInvestigationReasonIds"
                  label={t('cases.secondary_request_reason')}
                >
                  <ClassifierInfiniteSelect
                    id={reasonClassifierId}
                    mode="multiple"
                    placeholder={t('cases.secondary_request_reason')}
                    maxTagCount={2}
                    filterValues={[investigationReasonId]}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="specimenTypeId"
                label={t('consultations.specimen_type')}
                rules={requiredRules}
              >
                <ClassifierInfiniteSelect
                  id={cuatmClassifierId}
                  placeholder={t('consultations.specimen_type')}
                  onChange={() => {
                    if (selectedCategory.hasSelectedInvestigation) {
                      form.resetFields(['categories'])
                      resetTests()
                    }
                  }}
                />
              </Form.Item>

              {investigationReasonId && specimenTypeId && (
                <InvestigationCategories
                  form={form}
                  specimenTypeId={specimenTypeId}
                  isScreening
                  autoSelectCurrentLaboratory
                  onCategoryChange={resetTests}
                />
              )}

              {selectedCategory.hasSelectedInvestigation && (
                <Form.List
                  name={SCREENING_TESTS_FORM}
                  initialValue={[newScreeningTestRow()]}
                >
                  {(fields, { add, remove }) => (
                    <Flex vertical gap={16}>
                      {fields.map((field, index) => (
                        <ScreeningTestRow
                          key={field.key}
                          form={form}
                          field={field}
                          index={index}
                          investigationTypeOptions={investigationTypeOptions}
                          resultOptionClassifierId={resultOptionClassifierId}
                          isForeign={isForeign}
                          canRemove={fields.length > 1}
                          onRemove={() => remove(field.name)}
                        />
                      ))}

                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => add(newScreeningTestRow())}
                      >
                        {t('screening.add_test')}
                      </Button>
                    </Flex>
                  )}
                </Form.List>
              )}

              {selectedCategory.hasSelectedInvestigation && (
                <Form.Item
                  name="testedBy"
                  label={t('screening.tested_by')}
                  rules={requiredRules}
                  initialValue={session?.userId}
                >
                  <HivDoctorSelect allowClear={false} />
                </Form.Item>
              )}

              {hasAnyResult && (
                <Form.Item
                  name="postTestCounseling"
                  rules={requiredRules}
                  valuePropName="checked"
                >
                  <Checkbox>{t('cases.post_test_counseling')}</Checkbox>
                </Form.Item>
              )}

              <Flex justify="center" gap={8}>
                <Button onClick={onCancel}>{t('common.cancel')}</Button>

                <SaveConfirm
                  title={`${t('common.confirm')}?`}
                  onConfirm={onSubmit}
                  okButtonProps={{ loading: isPending }}
                >
                  <Button
                    type="primary"
                    disabled={!areTestsComplete || !testedBy}
                  >
                    {t('common.confirm')}
                  </Button>
                </SaveConfirm>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Form>
    </Modal>
  )
}
